import React, { useState, useEffect } from 'lib/react';
import {
  EVChip,
  EVClear,
  EVFormHelperText,
  EVGrid,
  EVInputLabel,
  EVTextField,
} from 'lib/components';
import { useTranslation } from 'lib/translation';
import ChipInput from 'commonComponents/chip-input';
import { EMAIL_REGEX } from 'constants/AppConstants';
import { useSelector, useDispatch } from 'lib/redux';
import { updateEmailCC, expandPanel } from 'features/finalizeOrder/FinalizeOrder.actions';
import clsx from 'clsx';
import { getUserDetails } from 'utils/app.utils';
import { Link } from 'lib/router';
import { CONTACT_INFORMATION_ROUTE } from 'constants/RouteConstants';
import EmailRecipientsStyles from './EmailRecipientsStyles';
import FinalizeOrderExpansionPanel from '../FinalizeOrderExpansionPanel.component';

function EmailRecipients() {
  const classes = EmailRecipientsStyles();
  const emailIDs = useSelector(({ placeOrder }) => placeOrder.emailCC);
  const {
    isExpanded, upgradeUserEmail,
  } = useSelector(({
    finalizeOrder,
  }) => ({
    isExpanded: finalizeOrder.isExpanded,
    upgradeUserEmail: finalizeOrder.upgradeUserEmail,
  }));
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const getPanelHeader = (header) => (<EVGrid>{header}</EVGrid>);
  const dispatch = useDispatch();

  const searchParams = new URLSearchParams(window.location.search);
  const upgradeReportId = searchParams.get('upgradeReportId');

  const handleEmailInputChange = (event, email) => {
    if (event?.type === 'change') setInputValue(email);
  };

  const setEmailIDs = (list) => dispatch(updateEmailCC(list));
  useEffect(() => {
    dispatch(expandPanel('emailRecipients', true));
  }, []);
  const handleEmailAdd = (list) => {
    setEmailIDs(list);
    setInputValue('');
  };

  const handleEmailDelete = (list) => setEmailIDs(list);

  const handleClear = (list) => {
    setEmailIDs(list);
    setInputValue('');
  };

  /**
   * Render the selected value.
   *
   * @param {T[]} value The `value` provided to the component.
   * @param {function} getTagProps A tag props getter.
   * @returns {ReactNode}
   */
  const renderTags = (values, getProps) => values.map((value, index) => {
    const { className, ...props } = getProps({ index });
    const invalid = !EMAIL_REGEX.test(value);

    return (
      <EVChip
        label={value}
        className={clsx(className, invalid && 'error')}
        deleteIcon={<EVClear className={clsx(classes.tagDeleteIcon, invalid && 'error')} />}
        {...props}
      />
    );
  });

  const hasInvalidEmailIDs = emailIDs?.some((value) => !EMAIL_REGEX.test(value));

  return (
    <FinalizeOrderExpansionPanel
      panelSummary={getPanelHeader(t('finalize_order__email_recipients'))}
      defaultExpanded
      expanded={isExpanded.emailRecipients}
      onChange={(_, expanded) => dispatch(expandPanel('emailRecipients', expanded))}
    >
      <EVGrid container direction="column" style={{ gap: '22px' }}>
        <EVInputLabel htmlFor="emailRecipientsReports" className={classes.emailRecipientsReportsLabel}>
          {t('finalize_order__email_recipients_reports')}
          {upgradeReportId
            ? (
              <b>
                {upgradeUserEmail}
                {' '}
              </b>
            )
            : <b>{`${getUserDetails()?.userName}. `}</b>}
          <Link
            className={classes.editContactInfo}
            to={CONTACT_INFORMATION_ROUTE}
            aria-label="EditContactInfo"
          >
            {t('finalize_order__edit_contact_info')}
          </Link>
        </EVInputLabel>
        <EVGrid container direction="column" style={{ gap: '8px' }}>
          <EVInputLabel htmlFor="emailRecipientsSendReport" className={classes.emailRecipientsReportsLabel}>
            {t('finalize_order__email_recipients_send_report')}
          </EVInputLabel>
          <ChipInput
            id="emailIds-input"
            inputValue={inputValue}
            onInputChange={handleEmailInputChange}
            renderInput={(props) => <EVTextField variant="outlined" {...props} />}
            onAddValue={handleEmailAdd}
            onDeleteValue={handleEmailDelete}
            onClear={handleClear}
            classes={{ inputRoot: clsx(classes.emailInputRoot, hasInvalidEmailIDs && 'error'), tag: classes.emailInputTag }}
            className={classes.emailInput}
            value={emailIDs}
            renderTags={renderTags}
          />
          {hasInvalidEmailIDs && (
          <EVFormHelperText className={classes.invalidEmail}>
            {t('finalize_order__invalid_email_address')}
          </EVFormHelperText>
          )}
        </EVGrid>
      </EVGrid>
    </FinalizeOrderExpansionPanel>
  );
}

export default EmailRecipients;
